import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";
import { Button } from "@/components/ui/button";
import { ArrowRight, Square } from "lucide-react";

const groups = [
  {
    title: "BEFORE WE ARRIVE",
    items: [
      "Confirm your appointment time and ensure someone 18 or older is on-site to provide access for the full appointment window.",
      "Have your confirmation details ready and let us know of any access issues (gate codes, parking, building entry) in advance.",
      "Complete every preparation step below — incomplete preparation reduces treatment effectiveness and may require a return visit."
    ]
  },
  {
    title: "LAUNDER BEDDING & CLOTHING",
    items: [
      "Strip all bedding — sheets, pillowcases, duvet covers, mattress protectors — and place items directly into a plastic bag to carry to the laundry (do not drag them through the home).",
      "Wash in hot water (at least 60°C / 140°F) on the hottest cycle your fabrics allow.",
      "Dry on the highest heat setting for a minimum of 30–40 minutes. Heat, not water, is what kills bed bugs and eggs.",
      "Place all clean, dried items into new sealed plastic bags or airtight bins and keep them sealed until after the follow-up treatment. Do not return them to an untreated room.",
      "Repeat this process for all clothing stored within 6 feet of the bed, plus curtains and throw rugs near sleeping areas."
    ]
  },
  {
    title: "BED & BEDROOM FURNITURE",
    items: [
      "Pull beds and bedroom furniture at least 2 feet away from the walls so the technician can access baseboards and wall-floor junctions.",
      "Remove everything from under the bed and from beneath bedroom furniture — storage boxes, shoes, bags and clutter must go.",
      "Empty nightstands, dressers and bedside tables of all contents so interior surfaces can be treated.",
      "Stand mattresses and box springs upright against a wall (after stripping bedding) to expose the bed frame and headboard.",
      "Disassemble bed frames where possible (remove slats, legs, wheels) so joints and screw holes can be treated."
    ]
  },
  {
    title: "CLEAR THE TREATMENT AREA",
    items: [
      "Clear the perimeter of every room being treated — move furniture, boxes and belongings at least 2 feet from the walls so baseboards are reachable.",
      "Remove items from closet floors and shelves in treatment rooms so the technician can access the closet baseboards and corners.",
      "Take down wall-hung items within the treatment area (pictures, clocks, mirrors) and place them in a clean sealed bag — bed bugs hide behind wall hangings.",
      "Vacuum floors and baseboards before treatment, then empty or discard the vacuum bag outside immediately after."
    ]
  },
  {
    title: "PEOPLE, PETS & SENSITIVITIES",
    items: [
      "All people and pets must leave the property before treatment begins and stay out for a minimum of 4 hours after the technician completes the treatment (or longer if advised).",
      "Fish tanks and terrariums should be covered with a towel and the air pump turned off during treatment; return pumps to normal once you re-enter.",
      "Remove or cover food, dishes, utensils and pet bowls in treatment areas, or store them in the refrigerator or sealed cupboards.",
      "Tell the technician in advance about anyone in the household with asthma, respiratory conditions, pregnancy or chemical sensitivities so the treatment approach can be adjusted."
    ]
  },
  {
    title: "AFTER TREATMENT",
    items: [
      "Do not wash, mop or wipe treated baseboards, bed frames or seams for at least 2 weeks — the residual product keeps working during this window.",
      "Wait 4 hours (or as advised) before re-entering, then open windows for 15–20 minutes to ventilate before settling back in.",
      "Keep laundered bedding and clothing in their sealed bags until you are instructed it is safe to return them.",
      "Do not discard your mattress unless advised — most mattresses can be successfully treated and protected with an encasement.",
      "Attend the scheduled follow-up treatment; a single treatment rarely eliminates every egg, and the follow-up breaks the lifecycle."
    ]
  }
];

export default function Preparation() {
  return (
    <section id="preparation" className="border-b border-border bg-muted/30 px-5 py-24 lg:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeader
          kicker="Preparation"
          title="YOUR PREPARATION MATTERS."
          subtitle="Proper preparation is part of the treatment — not an optional extra. Completing every step below maximizes effectiveness and prevents bed bugs from surviving in untreated belongings."
        />
        <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {groups.map((g, i) => (
            <Reveal key={g.title} delay={(i % 3) * 0.06}>
              <div className="h-full rounded-lg border border-border bg-background p-7">
                <div className="flex items-center gap-2">
                  <Square className="h-3.5 w-3.5 fill-accent text-accent" />
                  <h3 className="font-heading text-xs font-bold uppercase tracking-[0.18em] text-accent">{g.title}</h3>
                </div>
                <ul className="mt-5 space-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex gap-2.5 text-sm leading-relaxed text-muted-foreground">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-10">
            <Button asChild size="lg" className="h-12 px-8 text-sm font-semibold tracking-wide bg-primary text-white hover:bg-primary/90">
              <a href="#booking">View Preparation Instructions <ArrowRight className="ml-1 h-4 w-4" /></a>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}